import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { clearLocalPlaceholder, getLocalPlaceholder } from './pending-uploads';
import { assetAvailability } from '../../services/asset-availability';

/**
 * Loader en video først når asset-availability-servicen har bekreftet at
 * `assetPath` finnes i library. Tidligere pollet vi selve URL-en og retry-et
 * på 404 (480 forsøk × 2 s) — det skapte 404-spam i serverlogger og en bug
 * der retry kunne lykkes uten at parent fikk beskjed. Nå abonnerer vi på
 * service-en, og setter `<source>` først når vi vet backend faktisk har
 * filen klar. Service-en polls library én gang per orgId mens vi venter.
 *
 * Hvis `assetPath` er tom, faller vi tilbake til umiddelbar lasting (samme
 * oppførsel som for image-elementet, brukbart for std-assets og preview).
 *
 * Optional placeholder fra pending-uploads-registret: hvis en lokal
 * thumbnail er registrert mot asset-URL-en rendrer vi den som `<img>` bak
 * videoen (ikke `poster` — Safari ignorerer ofte poster når autoplay er
 * på og source 404'er).
 *
 * Selve "lastes opp"-overlay rendrer vi IKKE her — d-slide-media gjør det
 * på slide-nivå (uavhengig av zoom/pan). Komponenten dispatcher
 * `video-loaded` når videoen er klar.
 *
 * Layout-agnostisk. Style host eller `<video>` via `::part(video)`.
 */
@customElement('d-loading-video')
export class DLoadingVideo extends LitElement {
  static readonly styles = css`
    :host {
      display: inline-block;
      position: relative;
      line-height: 0;
    }
    video {
      display: block;
    }
    img.placeholder {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
    }
    video.loaded ~ img.placeholder {
      display: none;
    }
  `;

  @property({ type: String })
  src = '';

  /**
   * Kanonisk path (f.eks. `org/abc.mp4`) — brukes til oppslag mot
   * asset-availability-servicen. Når blank: lastes umiddelbart uten å vente
   * på library-bekreftelse.
   */
  @property({ type: String, attribute: 'asset-path' })
  assetPath = '';

  @property({ type: Boolean })
  autoplay = false;

  @property({ type: Boolean })
  loop = true;

  @property({ type: Boolean })
  muted = true;

  @state() private loaded = false;
  // Default false: vent på subscribe-cb i connectedCallback før første render.
  // For tom assetPath gir service-en cb(true) synkront → ingen flash før det.
  @state() private ready = false;
  @state() private placeholder = '';

  @query('video') private videoEl?: HTMLVideoElement;

  private unsubscribe?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    this.subscribeToAvailability();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  protected updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('assetPath')) {
      this.subscribeToAvailability();
    }
    if (changedProps.has('src')) {
      this.loaded = false;
      this.placeholder = this.src ? (getLocalPlaceholder(this.src) ?? '') : '';
    }
    // <video> må eksplisitt re-loades når <source>-src endrer seg, ellers
    // beholder den den gamle lasten.
    if ((changedProps.has('src') || changedProps.has('ready')) && this.ready && this.src) {
      this.videoEl?.load();
    }
  }

  private subscribeToAvailability() {
    this.unsubscribe?.();
    this.unsubscribe = assetAvailability.subscribe(this.assetPath, (ready) => {
      this.ready = ready;
    });
  }

  private onLoaded = () => {
    if (!this.ready || !this.src) return;
    this.loaded = true;
    this.placeholder = '';
    if (this.src) clearLocalPlaceholder(this.src);
    const video = this.videoEl;
    if (video && video.videoWidth > 0 && video.videoHeight > 0) {
      this.dispatchEvent(
        new CustomEvent<{ naturalWidth: number; naturalHeight: number; duration: number }>('video-loaded', {
          bubbles: true,
          composed: true,
          detail: {
            naturalWidth: video.videoWidth,
            naturalHeight: video.videoHeight,
            duration: Math.round(video.duration * 100) / 100,
          },
        }),
      );
    }
  };

  render() {
    if (!this.src) return nothing;
    return html`
      ${this.ready
        ? html`
            <video
              part="video"
              class="${this.loaded ? 'loaded' : ''}"
              ?autoplay=${this.autoplay}
              ?loop=${this.loop}
              ?muted=${this.muted}
              playsinline
              preload="metadata"
              @loadeddata=${this.onLoaded}
            >
              <source src="${this.src}" />
            </video>
          `
        : nothing}
      ${this.placeholder && !this.loaded
        ? html`<img class="placeholder" src="${this.placeholder}" alt="" aria-hidden="true" draggable="false" />`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-loading-video': DLoadingVideo;
  }
}
